import { forwardRef, useId, useState } from 'react';
import { Popover } from 'radix-ui';
import { Checkbox } from './Checkbox';

export interface MultiSelectOption {
  value: string;
  label: string;
}

/**
 * Trigger surface, mirroring `Select`'s `SELECT_SURFACE` exactly so the two
 * controls sit on one filter row without one of them staying white while the
 * console is on the dark theme. `bg-white`/`text-navy`/`border-slate` were
 * literals, which no `[data-theme]` override can reach; the tokens below carry
 * byte-identical LIGHT values (`--surface-card` #FFFFFF, `--text-primary`
 * #001928, `--text-muted` #A0AEBC = `--color-slate`), so member screens using
 * this primitive render unchanged. Only the translucent disabled/placeholder
 * tones, which have no flat-token equivalent, keep their brand values and get
 * an `admin-dark:` override.
 */
const TRIGGER_SURFACE =
  'border-text-muted bg-surface-card text-text-primary disabled:border-teal/5 disabled:bg-teal/5 ' +
  'admin-dark:border-border-strong admin-dark:disabled:border-border-subtle admin-dark:disabled:bg-surface-raised';

/**
 * Popover panel. `surface-overlay` rather than `surface-card`: the panel floats
 * above the console, and dark mode gives overlays their own lighter step
 * (#1F2430) so a dropdown does not disappear into the card behind it.
 */
const PANEL_SURFACE = 'border-text-muted bg-surface-overlay admin-dark:border-border-strong';

export interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  /** e.g. a max-selection cap - disables the checkbox for options not already selected. */
  isOptionDisabled?: (value: string) => boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

/**
 * Checkbox-list dropdown (mnr-standards: complex interactive widgets use a
 * Radix primitive). Built on Radix `Popover`, matching `Combobox`'s shape -
 * a trigger styled like `Select` (same border/radius/caret/placeholder
 * tone), opening a panel of `Checkbox` rows instead of a native option list.
 * Reuse target for any "multiple values from a short reference list" field
 * (e.g. Subsectors, MYN-46) that would otherwise be a bare checkbox group
 * floating outside the dropdown-styled field grid.
 */
export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(function MultiSelect(
  {
    label,
    options,
    selectedValues,
    onToggle,
    isOptionDisabled,
    disabled = false,
    error,
    placeholder,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const errorId = useId();
  const listboxId = useId();
  const hasError = Boolean(error);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);

  return (
    <div className="flex flex-col gap-1">
      <Popover.Root open={open && !disabled} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            ref={ref}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-label={label}
            aria-describedby={hasError ? errorId : undefined}
            disabled={disabled}
            className={`rounded-input text-input focus:border-green relative w-full truncate border py-2 pr-10 pl-4 text-left focus:outline-none disabled:cursor-not-allowed ${TRIGGER_SURFACE}`}
          >
            {selectedLabels.length > 0 ? (
              selectedLabels.join(', ')
            ) : (
              <span className="text-teal/70 admin-dark:text-text-muted">
                {placeholder ?? label}
              </span>
            )}
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              aria-hidden="true"
              className="text-text-secondary pointer-events-none absolute top-1/2 right-4 -translate-y-1/2"
            >
              <path d="M0 0h10L5 6z" fill="currentColor" />
            </svg>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            className={`rounded-button z-50 max-h-72 w-max max-w-[20rem] min-w-(--radix-popover-trigger-width) overflow-auto border p-2 whitespace-nowrap shadow-lg ${PANEL_SURFACE}`}
            sideOffset={4}
          >
            {options.length === 0 && (
              <p className="text-text-muted px-2 py-1.5 text-sm">No options</p>
            )}
            <div className="flex flex-col">
              {options.map((option) => (
                // A div, not a label: `Checkbox` renders its own `label`, and
                // nesting labels is invalid and breaks click-to-toggle.
                <div
                  key={option.value}
                  // `bg-zebra` was not a defined token, so the row had no hover
                  // feedback at all in either theme. `row-hover` is the one the
                  // console's table rows already use, and it is theme-aware.
                  className="hover:bg-row-hover flex items-center rounded-md px-2 py-1.5 transition-colors has-[input:disabled]:hover:bg-transparent"
                >
                  <Checkbox
                    label={option.label}
                    checked={selectedValues.includes(option.value)}
                    disabled={isOptionDisabled?.(option.value) ?? false}
                    onChange={() => onToggle(option.value)}
                  />
                </div>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {hasError && (
        <p id={errorId} role="alert" className="text-red pl-1 text-xs">
          {error}
        </p>
      )}
    </div>
  );
});
